import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { ResponsiveBar } from "@nivo/bar";
import dayjs from "dayjs";
import io from "socket.io-client";
import Header from "../../components/Header";
import { tokens } from "../../theme";
import {
  getDatoClasificacion,
  getDatoInterfoleacion,
  getDatoReporte,
} from "../../services/graficos.services";
import getUrlSocket from "../../utils/getUrlSocket";
import ModalCharge from "../../modal/modalCharge";
import secureStorage from "../../utils/secureStorage";

const SOCKET_URL = getUrlSocket();
const BRAND_BLUE = "#0077c8";
const BRAND_ORANGE = "#ff6105";
const VOICE_STORAGE_KEY = "rankingCelebrationVoiceURI";

const TURNOS = [
  { id: 3, label: "Primer Turno", minutos: 480 },
  { id: 4, label: "Segundo Turno", minutos: 510 },
  { id: 12, label: "Tercer Turno", minutos: 450 },
];

const TURNO_MAP = {
  3: "Primer Turno",
  4: "Segundo Turno",
  12: "Tercer Turno",
};

const parseCantidad = (row) => {
  const qty = Number(row?.total_cantidad ?? row?.cantidad);
  if (!Number.isNaN(qty)) return qty;
  return 1;
};

const normalizeTurnoId = (value) => {
  if (value === null || value === undefined || value === "") return null;

  const parsed = Number(value);
  if (!Number.isNaN(parsed) && TURNO_MAP[parsed]) return parsed;

  const text = String(value).toLowerCase();
  if (text.includes("primer") || text.includes("1er")) return 3;
  if (text.includes("segundo") || text.includes("2do")) return 4;
  if (text.includes("tercer") || text.includes("3er")) return 12;
  return null;
};

const rowEsDefecto = (row) => {
  const idClasificacion = Number(row?.id_clasificacion);
  if (!Number.isNaN(idClasificacion)) return idClasificacion !== 1;

  const clasificacion = String(row?.clasificacion ?? "").toLowerCase();
  if (clasificacion.includes("tipo a")) return false;

  const obs = String(row?.obs ?? "").toLowerCase();
  if (obs.includes("sin defect")) return false;

  return true;
};

const baseTurnoMap = () => ({
  3: 0,
  4: 0,
  12: 0,
});

const getFechaOperativaActual = () => {
  const ahora = dayjs();
  if (ahora.hour() < 3) {
    return ahora.subtract(1, "day").endOf("day");
  }
  return ahora.endOf("day");
};

const getTurnoMinutesInRange = (start, end, turnoId) => {
  const turno = TURNOS.find((item) => item.id === turnoId);
  if (!turno) return 0;

  const startDay = dayjs(start).startOf("day");
  const endDay = dayjs(end).startOf("day");

  if (endDay.isBefore(startDay)) return 0;

  const totalDays = endDay.diff(startDay, "day") + 1;

  // El 3er turno se cuenta desde el dia de inicio del turno, aunque termine a las 03:00 del dia siguiente.
  return totalDays * turno.minutos;
};

const buildMotivationalMessage = (liderActual, ventaja = 0, esPrueba = false) => {
  const ventajaTexto = Math.max(0, Number(ventaja) || 0);

  if (esPrueba) {
    return `Excelente energia de equipo. ${liderActual} esta marcando el ritmo. Mantengan enfoque y disciplina para cerrar un turno historico.`;
  }

  if (ventajaTexto >= 25) {
    return `Impresionante ${liderActual}. Tomaron la punta con ${ventajaTexto} chapas de ventaja. Sigamos asi para romper la meta del turno.`;
  }

  if (ventajaTexto >= 10) {
    return `Excelente trabajo ${liderActual}. Lideran con ${ventajaTexto} chapas de ventaja. Mantengan la intensidad y el orden.`;
  }

  return `Gran avance ${liderActual}. La punta cambio y cada chapa cuenta. Vamos con todo para consolidar este liderazgo.`;
};

const RankingProduccion = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const rankingLeadersRef = useRef({
    cantidad: "",
    velocidad: "",
    comparacionInter: "",
    calidadCalandra: "",
    calidadInter: "",
  });
  const celebrationTimeoutRef = useRef(null);
  const fechaOperativaActual = getFechaOperativaActual();

  const [startDate, setStartDate] = useState(fechaOperativaActual.startOf("day"));
  const [endDate, setEndDate] = useState(fechaOperativaActual);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [rankingProduccion, setRankingProduccion] = useState([]);
  const [rankingVelocidad, setRankingVelocidad] = useState([]);
  const [rankingCalidadCalandra, setRankingCalidadCalandra] = useState([]);
  const [rankingCalidadInter, setRankingCalidadInter] = useState([]);
  const [tablaInterfoliacion, setTablaInterfoliacion] = useState([]);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationTitle, setCelebrationTitle] = useState("Celebracion");
  const [celebrationMessage, setCelebrationMessage] = useState("");
  const [celebrationKey, setCelebrationKey] = useState(0);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [selectedVoiceURI, setSelectedVoiceURI] = useState(() => {
    if (typeof window === "undefined") return "auto";
    return secureStorage.getItem(VOICE_STORAGE_KEY) || "auto";
  });

  const payloadFechas = useMemo(
    () => ({
      fechaInicio: startDate.format("YYYY-MM-DD"),
      fechaFin: endDate.format("YYYY-MM-DD"),
    }),
    [startDate, endDate]
  );

  const loadRanking = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const [produccionRows, clasificacionRows, interRows] = await Promise.all([
        getDatoReporte(payloadFechas),
        getDatoClasificacion(payloadFechas),
        getDatoInterfoleacion(payloadFechas),
      ]);

      const produccionPorTurno = baseTurnoMap();
      (Array.isArray(produccionRows) ? produccionRows : []).forEach((row) => {
        const turnoId = normalizeTurnoId(row?.id_horario ?? row?.turno ?? row?.turnos);
        if (!turnoId) return;
        produccionPorTurno[turnoId] += parseCantidad(row);
      });

      const produccionRanking = TURNOS.map((turno) => ({
        turnoId: turno.id,
        turno: turno.label,
        cantidad: produccionPorTurno[turno.id] || 0,
      })).sort((a, b) => b.cantidad - a.cantidad);

      const velocidadRanking = TURNOS.map((turno) => {
        const cantidadTurno = produccionPorTurno[turno.id] || 0;
        const minutosPeriodo = getTurnoMinutesInRange(startDate, endDate, turno.id);
        const turnosPeriodo = minutosPeriodo > 0 ? minutosPeriodo / turno.minutos : 0;
        const porMinuto = minutosPeriodo > 0 ? cantidadTurno / minutosPeriodo : 0;
        const porHora = porMinuto * 60;

        return {
          turnoId: turno.id,
          turno: turno.label,
          porMinuto,
          porHora,
          porTurno: cantidadTurno,
          turnosPeriodo,
        };
      }).sort((a, b) => b.porHora - a.porHora);

      const defectosCalandra = baseTurnoMap();
      (Array.isArray(clasificacionRows) ? clasificacionRows : []).forEach((row) => {
        if (!rowEsDefecto(row)) return;
        const turnoId = normalizeTurnoId(row?.turnos ?? row?.turno ?? row?.id_horario);
        if (!turnoId) return;
        defectosCalandra[turnoId] += parseCantidad(row);
      });

      const interProduccionPorTurno = baseTurnoMap();
      const defectosInter = baseTurnoMap();
      (Array.isArray(interRows) ? interRows : []).forEach((row) => {
        const turnoId = normalizeTurnoId(row?.turno ?? row?.turnos ?? row?.id_horario);
        if (!turnoId) return;

        const cantidadInter = parseCantidad(row);
        interProduccionPorTurno[turnoId] += cantidadInter;

        if (!rowEsDefecto(row)) return;
        defectosInter[turnoId] += cantidadInter;
      });

      const calidadCalandraRanking = TURNOS.map((turno) => ({
        turnoId: turno.id,
        turno: turno.label,
        defectos: defectosCalandra[turno.id] || 0,
      })).sort((a, b) => a.defectos - b.defectos);

      const calidadInterRanking = TURNOS.map((turno) => ({
        turnoId: turno.id,
        turno: turno.label,
        defectos: defectosInter[turno.id] || 0,
      })).sort((a, b) => a.defectos - b.defectos);

      const tablaInter = TURNOS.map((turno) => {
        const producidas = interProduccionPorTurno[turno.id] || 0;
        const defectuosas = defectosInter[turno.id] || 0;
        const buenas = Math.max(producidas - defectuosas, 0);
        const calidad = producidas > 0 ? (buenas / producidas) * 100 : 0;

        return {
          turnoId: turno.id,
          turno: turno.label,
          producidas,
          defectuosas,
          buenas,
          calidad,
        };
      }).sort((a, b) => b.producidas - a.producidas);

      setRankingProduccion(produccionRanking);
      setRankingVelocidad(velocidadRanking);
      setRankingCalidadCalandra(calidadCalandraRanking);
      setRankingCalidadInter(calidadInterRanking);
      setTablaInterfoliacion(tablaInter);
    } catch (fetchError) {
      console.error("Error al cargar ranking de produccion:", fetchError);
      setError("No se pudo cargar el ranking de produccion");
      setRankingProduccion([]);
      setRankingVelocidad([]);
      setRankingCalidadCalandra([]);
      setRankingCalidadInter([]);
      setTablaInterfoliacion([]);
    } finally {
      setLoading(false);
    }
  }, [endDate, payloadFechas, startDate]);

  useEffect(() => {
    loadRanking();
  }, [loadRanking]);

  useEffect(() => {
    const socket = io(SOCKET_URL, {
      withCredentials: true,
      transports: ["websocket", "polling"],
      query: {
        token: secureStorage.getItem("authToken"),
      },
    });

    const handleProduccionUpdated = () => {
      loadRanking();
    };

    socket.on("ProduccionUpdated", handleProduccionUpdated);

    return () => {
      socket.off("ProduccionUpdated", handleProduccionUpdated);
      socket.disconnect();
    };
  }, [loadRanking]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;

    const synth = window.speechSynthesis;

    const syncVoices = () => {
      const voices = synth.getVoices() || [];
      setAvailableVoices(voices);
    };

    syncVoices();
    synth.addEventListener("voiceschanged", syncVoices);

    return () => {
      synth.removeEventListener("voiceschanged", syncVoices);
    };
  }, []);

  const handleVoiceChange = (event) => {
    const newVoice = event.target.value;
    setSelectedVoiceURI(newVoice);
    if (typeof window !== "undefined") {
      secureStorage.setItem(VOICE_STORAGE_KEY, newVoice);
    }
  };

  const triggerCelebration = useCallback((title, message) => {
    setCelebrationTitle(title);
    setCelebrationMessage(message);
    setShowCelebration(true);
    setCelebrationKey((prev) => prev + 1);

    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(message);
      const selectedVoice =
        selectedVoiceURI === "auto"
          ? availableVoices.find((voice) => voice.lang?.toLowerCase().startsWith("es")) ||
            availableVoices[0]
          : availableVoices.find((voice) => voice.voiceURI === selectedVoiceURI);

      if (selectedVoice) {
        utterance.voice = selectedVoice;
        utterance.lang = selectedVoice.lang;
      } else {
        utterance.lang = "es-ES";
      }
      utterance.rate = 0.95;
      utterance.pitch = 1;
      utterance.volume = 1;
      window.speechSynthesis.speak(utterance);
    }

    if (celebrationTimeoutRef.current) {
      clearTimeout(celebrationTimeoutRef.current);
    }

    celebrationTimeoutRef.current = setTimeout(() => {
      setShowCelebration(false);
    }, 60000);
  }, [availableVoices, selectedVoiceURI]);

  const handleTestCelebration = () => {
    const liderActual = rankingProduccion[0]?.turno || "el turno lider";
    const ventajaPrueba = Math.max(
      0,
      (rankingProduccion[0]?.cantidad || 0) - (rankingProduccion[1]?.cantidad || 0)
    );
    triggerCelebration(
      "Prueba de celebracion",
      buildMotivationalMessage(liderActual, ventajaPrueba, true)
    );
  };

  useEffect(() => {
    const updateLeader = (key, nextLeader) => {
      if (!nextLeader) return { changed: false, initialized: false };

      const previous = rankingLeadersRef.current[key];
      if (!previous) {
        rankingLeadersRef.current[key] = nextLeader;
        return { changed: false, initialized: true };
      }

      if (previous !== nextLeader) {
        rankingLeadersRef.current[key] = nextLeader;
        return { changed: true, initialized: false };
      }

      rankingLeadersRef.current[key] = nextLeader;
      return { changed: false, initialized: false };
    };

    const liderCantidad = rankingProduccion[0]?.turno || "";
    const cantidadResult = updateLeader("cantidad", liderCantidad);
    if (cantidadResult.changed) {
      const ventaja = Math.max(
        0,
        (rankingProduccion[0]?.cantidad || 0) - (rankingProduccion[1]?.cantidad || 0)
      );
      triggerCelebration(
        "1) Ranking de cantidad",
        buildMotivationalMessage(liderCantidad, ventaja)
      );
      return;
    }

    const liderVelocidad = rankingVelocidad[0]?.turno || "";
    const velocidadResult = updateLeader("velocidad", liderVelocidad);
    if (velocidadResult.changed) {
      triggerCelebration(
        "2) Ranking de velocidad",
        `Nuevo lider en velocidad: ${liderVelocidad}. Excelente ritmo de trabajo, mantengan la eficiencia del turno.`
      );
      return;
    }

    const liderComparacion = tablaInterfoliacion[0]?.turno || "";
    const comparacionResult = updateLeader("comparacionInter", liderComparacion);
    if (comparacionResult.changed) {
      triggerCelebration(
        "3) Comparacion por turno",
        `${liderComparacion} lidera la comparacion de Interfoliacion. Sigan sosteniendo la calidad y el volumen.`
      );
      return;
    }

    const liderCalandra = rankingCalidadCalandra[0]?.turno || "";
    const calandraResult = updateLeader("calidadCalandra", liderCalandra);
    if (calandraResult.changed) {
      triggerCelebration(
        "4) Calidad",
        `Cambio de lider en calidad Calandra: ${liderCalandra}. Menos defectos, mejor resultado de equipo.`
      );
      return;
    }

    const liderInter = rankingCalidadInter[0]?.turno || "";
    const interResult = updateLeader("calidadInter", liderInter);
    if (interResult.changed) {
      triggerCelebration(
        "4) Calidad",
        `Cambio de lider en calidad Interfoliacion: ${liderInter}. Excelente control del proceso, sigan asi.`
      );
    }
  }, [
    rankingCalidadCalandra,
    rankingCalidadInter,
    rankingProduccion,
    rankingVelocidad,
    tablaInterfoliacion,
    triggerCelebration,
  ]);

  useEffect(() => {
    return () => {
      if (celebrationTimeoutRef.current) {
        clearTimeout(celebrationTimeoutRef.current);
      }
      if (typeof window !== "undefined" && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const maxProduccion = useMemo(
    () => Math.max(...rankingProduccion.map((item) => item.cantidad), 1),
    [rankingProduccion]
  );

  const interComparativoChartData = useMemo(
    () => {
      const byTurno = tablaInterfoliacion.reduce((acc, row) => {
        acc[row.turno] = row;
        return acc;
      }, {});

      const primerTurno = byTurno["Primer Turno"] || {};
      const segundoTurno = byTurno["Segundo Turno"] || {};
      const tercerTurno = byTurno["Tercer Turno"] || {};

      return [
        {
          indicador: "Producidas",
          "Primer Turno": primerTurno.producidas || 0,
          "Segundo Turno": segundoTurno.producidas || 0,
          "Tercer Turno": tercerTurno.producidas || 0,
        },
        {
          indicador: "Defectuosas",
          "Primer Turno": primerTurno.defectuosas || 0,
          "Segundo Turno": segundoTurno.defectuosas || 0,
          "Tercer Turno": tercerTurno.defectuosas || 0,
        },
        {
          indicador: "Buenas",
          "Primer Turno": primerTurno.buenas || 0,
          "Segundo Turno": segundoTurno.buenas || 0,
          "Tercer Turno": tercerTurno.buenas || 0,
        },
      ];
    },
    [tablaInterfoliacion]
  );

  return (
    <Box
      sx={{
        height: "90vh",
        overflowY: "auto",
        padding: { xs: "10px", md: "16px" },
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          flexDirection: { xs: "column", md: "row" },
          gap: "12px",
          marginBottom: "12px",
        }}
      >
        <Header
          title="Ranking de Produccion por Turno"
          subtitle="Cantidad, velocidad y calidad en Calandra e Interfoliacion"
        />

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <DatePicker
              label="Inicio"
              value={startDate}
              onChange={(value) => value && setStartDate(value)}
              format="DD/MM/YYYY"
              slotProps={{
                textField: {
                  size: "small",
                  sx: { width: "145px" },
                },
              }}
            />
            <DatePicker
              label="Fin"
              value={endDate}
              onChange={(value) => value && setEndDate(value)}
              format="DD/MM/YYYY"
              slotProps={{
                textField: {
                  size: "small",
                  sx: { width: "145px" },
                },
              }}
            />
          </Box>
        </LocalizationProvider>

        <Button
          variant="contained"
          onClick={handleTestCelebration}
          sx={{
            backgroundColor: BRAND_ORANGE,
            color: "#ffffff",
            fontWeight: 700,
            "&:hover": {
              backgroundColor: "#e05604",
            },
          }}
        >
          Probar Celebracion
        </Button>

        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel id="voice-select-label">Voz Celebracion</InputLabel>
          <Select
            labelId="voice-select-label"
            value={selectedVoiceURI}
            label="Voz Celebracion"
            onChange={handleVoiceChange}
          >
            <MenuItem value="auto">Automatica (espanol)</MenuItem>
            {availableVoices.map((voice) => (
              <MenuItem key={voice.voiceURI} value={voice.voiceURI}>
                {`${voice.name} (${voice.lang})`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {showCelebration && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            pointerEvents: "none",
            zIndex: 2500,
            overflow: "hidden",
            "@keyframes fireworkBurst": {
              "0%": { transform: "translate(-50%, -50%) scale(0.25)", opacity: 1 },
              "100%": { transform: "translate(-50%, -50%) scale(1.4)", opacity: 0 },
            },
            "@keyframes fireworkRay": {
              "0%": { transform: "translate(-50%, -50%) rotate(var(--angle)) translateX(0px)", opacity: 1 },
              "100%": { transform: "translate(-50%, -50%) rotate(var(--angle)) translateX(var(--distance))", opacity: 0 },
            },
            "@keyframes confettiFall": {
              "0%": { transform: "translateY(-10vh) rotate(0deg)", opacity: 0 },
              "10%": { opacity: 1 },
              "100%": { transform: "translateY(105vh) rotate(520deg)", opacity: 0 },
            },
            "@keyframes bannerPop": {
              "0%": { transform: "translateX(-50%) scale(0.7)", opacity: 0 },
              "30%": { transform: "translateX(-50%) scale(1.06)", opacity: 1 },
              "100%": { transform: "translateX(-50%) scale(1)", opacity: 1 },
            },
            "@keyframes glowPulse": {
              "0%": { boxShadow: "0 0 0 0 rgba(255,97,5,0.45)" },
              "70%": { boxShadow: "0 0 0 22px rgba(0,119,200,0)" },
              "100%": { boxShadow: "0 0 0 0 rgba(255,97,5,0)" },
            },
          }}
        >
          {Array.from({ length: 8 }).map((_, burstIndex) => {
            const top = 12 + ((burstIndex * 17 + celebrationKey * 9) % 62);
            const left = 8 + ((burstIndex * 19 + celebrationKey * 13) % 84);

            return (
              <Box
                key={`burst-${celebrationKey}-${burstIndex}`}
                sx={{
                  position: "absolute",
                  top: `${top}%`,
                  left: `${left}%`,
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  background: burstIndex % 2 === 0 ? BRAND_ORANGE : BRAND_BLUE,
                  animation: "fireworkBurst 2.2s ease-out infinite",
                }}
              >
                {Array.from({ length: 16 }).map((__, rayIndex) => {
                  const angle = `${rayIndex * 22.5}deg`;
                  const distance = `${65 + (rayIndex % 4) * 16}px`;
                  const colorSet = [BRAND_ORANGE, BRAND_BLUE, "#48a8e8", "#ffa15f"];

                  return (
                    <Box
                      key={`ray-${celebrationKey}-${burstIndex}-${rayIndex}`}
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        width: "5px",
                        height: "5px",
                        borderRadius: "50%",
                        backgroundColor: colorSet[(rayIndex + burstIndex) % colorSet.length],
                        "--angle": angle,
                        "--distance": distance,
                        animation: "fireworkRay 2.2s ease-out infinite",
                      }}
                    />
                  );
                })}
              </Box>
            );
          })}

          {Array.from({ length: 28 }).map((_, confettiIndex) => {
            const left = (confettiIndex * 13 + celebrationKey * 5) % 100;
            const duration = 2.6 + (confettiIndex % 7) * 0.2;
            const delay = (confettiIndex % 9) * 0.08;
            const colorsSet = [BRAND_BLUE, BRAND_ORANGE, "#48a8e8", "#ffa15f"];

            return (
              <Box
                key={`confetti-${celebrationKey}-${confettiIndex}`}
                sx={{
                  position: "absolute",
                  top: "-12vh",
                  left: `${left}%`,
                  width: "8px",
                  height: `${10 + (confettiIndex % 3) * 4}px`,
                  borderRadius: "2px",
                  backgroundColor: colorsSet[confettiIndex % colorsSet.length],
                  animation: `confettiFall ${duration}s linear ${delay}s infinite`,
                  opacity: 0.95,
                }}
              />
            );
          })}

          <Box
            sx={{
              position: "absolute",
              top: "9%",
              left: "50%",
              transform: "translateX(-50%)",
              background: `linear-gradient(135deg, ${BRAND_BLUE} 0%, ${BRAND_ORANGE} 100%)`,
              color: "#ffffff",
              borderRadius: "16px",
              padding: "14px 18px",
              fontWeight: 800,
              boxShadow: "0 10px 30px rgba(0,0,0,0.38)",
              textAlign: "center",
              maxWidth: "86vw",
              border: "1px solid rgba(255,255,255,0.35)",
              animation: "bannerPop 0.45s ease-out forwards, glowPulse 1.35s ease-out 0.2s 40",
            }}
          >
            <Typography sx={{ fontSize: "1.02rem", fontWeight: 900, letterSpacing: "0.02em", marginBottom: "2px" }}>
              {celebrationTitle}
            </Typography>
            {celebrationMessage}
          </Box>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ marginBottom: "12px" }}>
          {error}
        </Alert>
      )}

      <ModalCharge isLoading={loading} />

      <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <Paper sx={{ backgroundColor: colors.primary[400], padding: "14px" }}>
            <Typography sx={{ fontSize: "1.2rem", fontWeight: 700, marginBottom: "10px" }}>
              1) Ranking de cantidad de vidrio producido
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {rankingProduccion.map((item, index) => (
                <Box key={item.turno}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "0.92rem",
                      marginBottom: "4px",
                    }}
                  >
                    <Typography sx={{ fontWeight: 700 }}>
                      {`${index + 1}. ${item.turno}`}
                    </Typography>
                    <Typography sx={{ fontWeight: 700 }}>{`${item.cantidad} chapas`}</Typography>
                  </Box>
                  <Box sx={{ background: colors.primary[500], borderRadius: "999px", height: "12px" }}>
                    <Box
                      sx={{
                        width: `${(item.cantidad / maxProduccion) * 100}%`,
                        height: "100%",
                        borderRadius: "999px",
                        background: `linear-gradient(90deg, ${BRAND_BLUE} 0%, ${BRAND_ORANGE} 100%)`,
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>

          <Paper sx={{ backgroundColor: colors.primary[400], padding: "14px" }}>
            <Typography sx={{ fontSize: "1.2rem", fontWeight: 700, marginBottom: "10px" }}>
              2) Ranking de velocidad de produccion
            </Typography>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" }, gap: "10px" }}>
              {rankingVelocidad.map((item, index) => (
                <Box
                  key={item.turno}
                  sx={{
                    backgroundColor: colors.primary[500],
                    borderRadius: "10px",
                    padding: "12px",
                    border: `1px solid ${colors.grey[700]}`,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
                    <Box
                      sx={{
                        width: "26px",
                        height: "26px",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 800,
                        color: "#fff",
                        background: `linear-gradient(135deg, ${BRAND_BLUE} 0%, ${BRAND_ORANGE} 100%)`,
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Typography sx={{ fontWeight: 700 }}>{item.turno}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: "0.9rem" }}>{`Por minuto: ${item.porMinuto.toFixed(3)} chapas/min`}</Typography>
                  <Typography sx={{ fontSize: "0.9rem" }}>{`Por hora: ${item.porHora.toFixed(2)} chapas/h`}</Typography>
                  <Typography sx={{ fontSize: "0.9rem" }}>{`Promedio por turno: ${(item.turnosPeriodo > 0 ? item.porTurno / item.turnosPeriodo : 0).toFixed(2)} chapas`}</Typography>
                  <Typography sx={{ fontSize: "0.9rem", color: "#9aa3c5" }}>{`Turnos considerados en el periodo: ${item.turnosPeriodo.toFixed(0)}`}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>

          <Paper sx={{ backgroundColor: colors.primary[400], padding: "14px" }}>
            <Typography sx={{ fontSize: "1.2rem", fontWeight: 700, marginBottom: "10px" }}>
              3) Comparacion por turno - Interfoliacion (Barras)
            </Typography>

            <Box sx={{ height: { xs: "320px", md: "380px" } }}>
              <ResponsiveBar
                data={interComparativoChartData}
                keys={["Primer Turno", "Segundo Turno", "Tercer Turno"]}
                indexBy="indicador"
                margin={{ top: 20, right: 20, bottom: 60, left: 60 }}
                padding={0.3}
                groupMode="grouped"
                valueScale={{ type: "linear" }}
                indexScale={{ type: "band", round: true }}
                colors={[BRAND_BLUE, "#2ab57d", BRAND_ORANGE]}
                borderRadius={4}
                borderColor={{ from: "color", modifiers: [["darker", 0.3]] }}
                axisTop={null}
                axisRight={null}
                axisBottom={{
                  tickSize: 5,
                  tickPadding: 6,
                  tickRotation: 0,
                  legend: "Indicadores",
                  legendPosition: "middle",
                  legendOffset: 42,
                }}
                axisLeft={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: "Cantidad",
                  legendPosition: "middle",
                  legendOffset: -48,
                }}
                enableLabel={true}
                labelSkipWidth={0}
                labelSkipHeight={0}
                labelTextColor="#ffffff"
                theme={{
                  axis: {
                    domain: { line: { stroke: colors.grey[100] } },
                    ticks: {
                      line: { stroke: colors.grey[100], strokeWidth: 1 },
                      text: { fill: colors.grey[100], fontSize: 12 },
                    },
                    legend: { text: { fill: colors.grey[100], fontSize: 12 } },
                  },
                  grid: {
                    line: { stroke: "rgba(255,255,255,0.12)", strokeWidth: 1 },
                  },
                  legends: { text: { fill: colors.grey[100] } },
                  tooltip: { container: { color: "#111" } },
                }}
                legends={[
                  {
                    dataFrom: "keys",
                    anchor: "bottom",
                    direction: "row",
                    justify: false,
                    translateX: 0,
                    translateY: 55,
                    itemsSpacing: 8,
                    itemWidth: 100,
                    itemHeight: 18,
                    itemDirection: "left-to-right",
                    symbolSize: 14,
                    symbolShape: "circle",
                  },
                ]}
                role="application"
                ariaLabel="Comparacion de produccion interfoliacion por turno"
              />
            </Box>
          </Paper>

          <Typography sx={{ fontSize: "1.2rem", fontWeight: 700, marginBottom: "-4px" }}>
            4) Calidad
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: "14px",
            }}
          >
            <Paper sx={{ backgroundColor: colors.primary[400], padding: "14px" }}>
              <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, marginBottom: "10px" }}>
                Calidad Calandra (menos defectos)
              </Typography>
              {rankingCalidadCalandra.map((item, index) => (
                <Box
                  key={item.turno}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "8px 10px",
                    borderRadius: "8px",
                    marginBottom: "6px",
                    backgroundColor: index === 0 ? "#1f5132" : colors.primary[500],
                  }}
                >
                  <Typography sx={{ fontWeight: 700 }}>{`${index + 1}. ${item.turno}`}</Typography>
                  <Typography sx={{ fontWeight: 700 }}>{`${item.defectos} chapas`}</Typography>
                </Box>
              ))}
            </Paper>

            <Paper sx={{ backgroundColor: colors.primary[400], padding: "14px" }}>
              <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, marginBottom: "10px" }}>
                Calidad Interfoliacion (menos defectos)
              </Typography>
              {rankingCalidadInter.map((item, index) => (
                <Box
                  key={item.turno}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "8px 10px",
                    borderRadius: "8px",
                    marginBottom: "6px",
                    backgroundColor: index === 0 ? "#1f5132" : colors.primary[500],
                  }}
                >
                  <Typography sx={{ fontWeight: 700 }}>{`${index + 1}. ${item.turno}`}</Typography>
                  <Typography sx={{ fontWeight: 700 }}>{`${item.defectos} chapas`}</Typography>
                </Box>
              ))}
            </Paper>
          </Box>
      </Box>
    </Box>
  );
};

export default RankingProduccion;
